/*
 * Modelos que se ven como ficha (ItemProfile) en vez de tabs de ViewContent.
 * Son modelos sin cálculos en el formulario y casi iguales en todos los tenants,
 * así que la disposición es código: los campos conocidos van a lugares fijos y
 * el resto se agrupa por su visual group (lo que agregue un tenant cae en su tarjeta).
 *
 * - avatarIcon: ícono del avatar cuando no hay imagen (null = iniciales del nombre)
 * - tagKeys: chips bajo el nombre
 * - metaKeys: renglón con ícono (ubicación, idioma, banco…)
 * - reachKeys: columna derecha del encabezado; kind email/phone/web abre, copy copia
 * - aboutKeys: textos largos, cada uno en su tarjeta arriba de las secciones
 * - sideGroups: visual groups que van en la columna derecha
 * - groupByKey: campo → visual group, cuando la definición lo pone en otro lado
 * - stats: tarjeta de cifras (columna derecha, arriba). `group` toma un visual
 *   group entero; `keys` arma una con esos campos. `icon` si el grupo no trae.
 * - filesSection: junta los campos de archivo en una tarjeta propia (derecha)
 * - hiddenKeys(item): campos que no van según el registro (p. ej. los números de
 *   nota que no corresponden a su tipo)
 * - inlineEdit: false = «Edit» abre el formulario completo; por defecto se edita
 *   en la ficha.
 * - hero: "transaction" = encabezado de movimiento de dinero: dirección
 *   (direction in/out, o directionKey + directionMap si sale de un campo),
 *   monto (amountKey + currencyKey), estado (statusKey o statusDateKey) y el
 *   recorrido from → to (campo + modelo de cada punta; con una sola, sin flecha).
 *   amountNoteKeys: cifras de solo lectura en un renglón chico bajo el monto.
 */
const LAYOUTS = {
  contact: {
    avatarIcon: null,
    tagKeys: ["type", "business_group", "reference"],
    metaKeys: [
      { key: "city", icon: "fas fa-city" },
      { key: "country", icon: "fas fa-globe-americas" },
      { key: "language", icon: "fas fa-language" },
    ],
    reachKeys: [
      { key: "emails", kind: "email" },
      { key: "phone", kind: "phone" },
      { key: "website", kind: "web" },
    ],
    aboutKeys: ["comments"],
    sideGroups: ["team", "finance_expenses"],
    // Hay tenants que los definen en General; en la ficha van con el equipo.
    groupByKey: { phone_logistic: "team", email_logistic: "team" },
    stats: null,
  },
  product: {
    avatarIcon: "fas fa-box",
    tagKeys: ["category", "referenceNumber"],
    metaKeys: [{ key: "countryOfOrigin", icon: "fas fa-globe-americas" }],
    reachKeys: [],
    aboutKeys: ["description", "comments", "observacionesProductoVendedor"],
    sideGroups: ["stock"],
    groupByKey: {},
    stats: { group: "stock", icon: "fas fa-boxes" },
  },
  bankaccount: {
    avatarIcon: "fas fa-university",
    tagKeys: ["currency"],
    metaKeys: [
      { key: "bank", icon: "fas fa-university" },
      { key: "city", icon: "fas fa-city" },
      { key: "country", icon: "fas fa-globe-americas" },
    ],
    reachKeys: [
      { key: "accountNumber", kind: "copy" },
      { key: "iban", kind: "copy" },
      { key: "swift", kind: "copy" },
    ],
    aboutKeys: ["comments"],
    sideGroups: ["intermediary", "beneficiary"],
    groupByKey: {},
    stats: {
      name: "_balances",
      label: "itemProfile.balances",
      icon: "fas fa-wallet",
      keys: ["initial_balance", "net_balance", "current_balance"],
    },
  },
};

// Pagos y cobros: casi iguales entre tenants. Las reglas del formulario
// (aplicado = suma de Applied to, saldo = monto − aplicado) corren también en la
// ficha al guardar (profileRules), y el back valida que no se aplique de más.
LAYOUTS.payment = {
  hero: "transaction",
  direction: "out",
  amountKey: "amount",
  currencyKey: "currency",
  statusKey: "paid",
  statusDateKey: "payment_date",
  from: { key: "bankaccount", model: "BankAccount" },
  to: { key: "contact", model: "Contact" },
  avatarIcon: null,
  tagKeys: ["type"],
  metaKeys: [
    { key: "date", icon: "far fa-calendar" },
    { key: "payment_date", icon: "fas fa-calendar-check" },
    { key: "invoice", icon: "fas fa-file-invoice" },
  ],
  reachKeys: [],
  aboutKeys: ["comment"],
  sideGroups: [],
  groupByKey: {},
  stats: null,
  // Aplicado y pendiente: renglón chico bajo el monto (una tarjeta era demasiado).
  amountNoteKeys: ["applied", "remaining"],
  filesSection: { label: "itemProfile.files", icon: "fas fa-paperclip" },
};

LAYOUTS.collect = {
  ...LAYOUTS.payment,
  direction: "in",
  statusKey: "received",
  statusDateKey: null,
  from: { key: "contact", model: "Contact" },
  to: { key: "bankaccount", model: "BankAccount" },
  metaKeys: [{ key: "date", icon: "far fa-calendar" }],
};

// Notas comerciales: la dirección sale de flow_direction (Giving = la emitimos,
// Receiving = la recibimos) y del otro lado solo está el contacto. Aplicado y saldo
// los recalcula el back al guardar (hook_aftersave_notacomercial).
LAYOUTS.notacomercial = {
  ...LAYOUTS.payment,
  directionKey: "flow_direction",
  directionMap: { Giving: "out", Receiving: "in" },
  kindLabels: { out: "itemProfile.noteGiving", in: "itemProfile.noteReceiving" },
  statusKey: null,
  statusDateKey: null,
  from: null,
  to: { key: "contact", model: "Contact" },
  tagKeys: ["credit_debit", "contact_role"],
  // Cada número al lado de su fecha; hiddenKeys deja solo el par que corresponde.
  metaKeys: [
    { key: "number", icon: "fas fa-hashtag" },
    { key: "number_date", icon: "fas fa-calendar-check" },
    { key: "number_credit", icon: "fas fa-hashtag" },
    { key: "number_credit_date", icon: "fas fa-calendar-check" },
    { key: "number_debit", icon: "fas fa-hashtag" },
    { key: "number_debit_date", icon: "fas fa-calendar-check" },
    { key: "date", icon: "far fa-calendar" },
  ],
  hiddenKeys: notaComercialHiddenNumberKeys,
  aboutKeys: ["description", "comment"],
  // La fila sin embarque ni proforma es la que guarda el resto sin aplicar: en la
  // vista parecía una aplicación «a nada». El resto ya se ve en «Falta aplicar»;
  // en el editor la fila sigue, que es donde se elige a qué aplicarla.
  hiddenViewRows: {
    applied_to: (row) =>
      !!row && typeof row === "object" && !refValue(row.shipping) && !refValue(row.proform),
  },
};

/*
 * Una nota tiene un solo número, como en el alta rápida del módulo: la que emitimos
 * (Giving) usa la secuencia de crédito o de débito (con Generate) y `number` se copia
 * solo; la que recibimos lleva el número del emisor en `number`. Los demás números y
 * sus fechas no se muestran. Sin dirección o tipo, se ven todos.
 */
const NOTA_NUMBER_KEYS = {
  number: ["number", "number_date"],
  credit: ["number_credit", "number_credit_date"],
  debit: ["number_debit", "number_debit_date"],
};

function notaComercialHiddenNumberKeys(item) {
  const flow = textlistFirst(item && item.flow_direction);
  const kind = textlistFirst(item && item.credit_debit);
  let keep = null;
  if (flow === "Receiving") keep = "number";
  else if (flow === "Giving" && kind === "Credit note") keep = "credit";
  else if (flow === "Giving" && kind === "Debit note") keep = "debit";
  if (!keep) return [];
  return Object.keys(NOTA_NUMBER_KEYS)
    .filter((k) => k !== keep)
    .flatMap((k) => NOTA_NUMBER_KEYS[k]);
}

function textlistFirst(v) {
  const first = Array.isArray(v) ? v[0] : v;
  return first == null ? "" : String(first).trim();
}

function refValue(v) {
  if (v && typeof v === "object") return v._id != null ? String(v._id) : "";
  return v ? String(v) : "";
}

export function getProfileLayout(modelName) {
  return LAYOUTS[String(modelName || "").toLowerCase()] || null;
}

export function hasItemProfile(modelName) {
  return !!getProfileLayout(modelName);
}

/*
 * «Vista anterior»: quien no se acomoda a la ficha (o tropieza con algo que la ficha
 * no resuelve) vuelve al detalle con pestañas y al formulario completo. Es una
 * preferencia de cada usuario en su navegador.
 */
const CLASSIC_VIEW_KEY = "gt.itemProfile.classicView";

export function prefersClassicView() {
  try {
    return localStorage.getItem(CLASSIC_VIEW_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

const CLASSIC_HINT_KEY = "gt.itemProfile.classicViewHintSeen";

/** El aviso «podés volver a la vista anterior» se muestra hasta que lo cierra. */
export function classicViewHintDismissed() {
  try {
    return localStorage.getItem(CLASSIC_HINT_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

export function dismissClassicViewHint() {
  try {
    localStorage.setItem(CLASSIC_HINT_KEY, "1");
  } catch (_e) {
    /* sin almacenamiento: vuelve a aparecer la próxima vez */
  }
}

export function setClassicViewPreference(classic) {
  try {
    if (classic) localStorage.setItem(CLASSIC_VIEW_KEY, "1");
    else localStorage.removeItem(CLASSIC_VIEW_KEY);
  } catch (_e) {
    /* sin almacenamiento: vale solo para esta pantalla */
  }
}

/** La ficha se edita en línea (si no, «Edit» abre el formulario completo). */
export function hasInlineProfileEdit(modelName) {
  const layout = getProfileLayout(modelName);
  return !!layout && layout.inlineEdit !== false;
}
